import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Plus, Trash2, Users, Clock, Edit2, X, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: "expected_litter", label: "Expected Litter", color: "bg-[#F472B6]" },
  { value: "stud_available", label: "Stud Available", color: "bg-[#3B82F6]" },
  { value: "mare_available", label: "Mare Available", color: "bg-[#8B5CF6]" },
  { value: "breeding_window", label: "Breeding Window", color: "bg-[#22C55E]" },
  { value: "unavailable", label: "Unavailable", color: "bg-[#71717A]" },
];

const SPECIES_OPTIONS = ["canine", "feline", "equine", "cattle", "exotics", "others"];

const BreederCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    event_type: "expected_litter",
    title: "",
    description: "",
    species: "canine",
    breed: "",
    start_date: "",
    end_date: "",
    waitlist_enabled: true,
    max_waitlist_spots: "",
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = sessionStorage.getItem("auth_token");
      const res = await axios.get(`${API}/api/breeder/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data.events || []);
    } catch (err) {
      logger.error("Failed to fetch calendar:", err);
      toast.error("Failed to load calendar events");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = sessionStorage.getItem("auth_token");
      const payload = {
        ...formData,
        max_waitlist_spots: formData.max_waitlist_spots ? parseInt(formData.max_waitlist_spots) : null,
      };

      if (editingEvent) {
        await axios.put(`${API}/api/breeder/calendar/${editingEvent.event_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/api/breeder/calendar`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event created");
      }

      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (err) {
      logger.error("Failed to save event:", err);
      toast.error(err.response?.data?.detail || "Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.delete(`${API}/api/breeder/calendar/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event deleted");
      fetchEvents();
    } catch (err) {
      logger.error("Failed to delete event:", err);
      toast.error("Failed to delete event");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      event_type: event.event_type,
      title: event.title,
      description: event.description || "",
      species: event.species,
      breed: event.breed || "",
      start_date: event.start_date,
      end_date: event.end_date || "",
      waitlist_enabled: event.waitlist_enabled,
      max_waitlist_spots: event.max_waitlist_spots?.toString() || "",
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      event_type: "expected_litter",
      title: "",
      description: "",
      species: "canine",
      breed: "",
      start_date: "",
      end_date: "",
      waitlist_enabled: true,
      max_waitlist_spots: "",
    });
  };

  const openNewEventModal = () => {
    setEditingEvent(null);
    resetForm();
    setShowModal(true);
  };

  const getEventTypeInfo = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];

  if (loading) {
    return (
      <div className="glass-card p-8">
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-8 bg-[#27272A] rounded w-1/3"></div>
          <div className="h-32 bg-[#27272A] rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl text-[#EDEDED] flex items-center gap-3">
            <CalendarIcon className="w-6 h-6 text-[#D4AF37]" />
            Breeding Calendar
          </h2>
          <p className="text-[#71717A] mt-1">Manage litter expectations, availability, and waitlists</p>
        </div>
        <button
          onClick={openNewEventModal}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Event
        </button>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <CalendarIcon className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="text-lg text-[#EDEDED] mb-2">No Events Yet</h3>
          <p className="text-[#71717A] mb-6">Add your first event to start managing your breeding calendar and waitlists.</p>
          <button
            onClick={openNewEventModal}
            className="px-6 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors"
          >
            Add Your First Event
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {events.map((event) => {
            const typeInfo = getEventTypeInfo(event.event_type);
            return (
              <div key={event.event_id} className="glass-card p-4 hover:border-[#D4AF37]/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className={`w-1 rounded-full ${typeInfo.color}`}></div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 ${typeInfo.color} text-black text-xs rounded-full`}>
                          {typeInfo.label}
                        </span>
                        <span className="text-xs text-[#71717A] capitalize">{event.species}</span>
                        {event.breed && <span className="text-xs text-[#71717A]">• {event.breed}</span>}
                      </div>
                      <h4 className="text-lg text-[#EDEDED]">{event.title}</h4>
                      {event.description && (
                        <p className="text-sm text-[#A1A1AA] mt-1">{event.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-[#71717A]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(event.start_date).toLocaleDateString()}
                          {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString()}`}
                        </span>
                        {event.waitlist_enabled && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {event.waitlist_count || 0} on waitlist
                            {event.max_waitlist_spots && ` / ${event.max_waitlist_spots} max`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(event)}
                      className="p-2 text-[#71717A] hover:text-[#EDEDED] transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(event.event_id)}
                      className="p-2 text-[#71717A] hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="glass-card w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl text-[#EDEDED]">
                {editingEvent ? "Edit Event" : "Add Event"}
              </h3>
              <button
                onClick={() => { setShowModal(false); setEditingEvent(null); }}
                className="p-2 text-[#71717A] hover:text-[#EDEDED]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Event Type</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  className="input-minimal"
                  required
                >
                  {EVENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Spring Litter - Golden Retrievers"
                  className="input-minimal"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add any additional details..."
                  className="input-minimal min-h-[80px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Species</label>
                  <select
                    value={formData.species}
                    onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                    className="input-minimal"
                    required
                  >
                    {SPECIES_OPTIONS.map((sp) => (
                      <option key={sp} value={sp}>{sp.charAt(0).toUpperCase() + sp.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Breed (optional)</label>
                  <input
                    type="text"
                    value={formData.breed}
                    onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                    placeholder="e.g., Golden Retriever"
                    className="input-minimal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="input-minimal"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">End Date (optional)</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="input-minimal"
                  />
                </div>
              </div>

              <div className="border-t border-[#27272A] pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.waitlist_enabled}
                    onChange={(e) => setFormData({ ...formData, waitlist_enabled: e.target.checked })}
                    className="sr-only"
                  />
                  <span className={`w-10 h-6 rounded-full transition-colors relative ${
                    formData.waitlist_enabled ? "bg-[#D4AF37]" : "bg-[#3F3F46]"
                  }`}>
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      formData.waitlist_enabled ? "left-5" : "left-1"
                    }`}></span>
                  </span>
                  <span className="text-[#EDEDED]">Enable Waitlist</span>
                </label>
              </div>

              {formData.waitlist_enabled && (
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Max Waitlist Spots (leave empty for unlimited)</label>
                  <input
                    type="number"
                    value={formData.max_waitlist_spots}
                    onChange={(e) => setFormData({ ...formData, max_waitlist_spots: e.target.value })}
                    placeholder="e.g., 10"
                    className="input-minimal"
                    min="1"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingEvent ? "Update Event" : "Create Event"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingEvent(null); }}
                  className="px-4 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreederCalendar;
